import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardHeader, CardContent } from "@shadcn/components/ui/card";


import { cva } from "class-variance-authority"; 
import {  CalendarDays, AlertCircle, Truck, CheckCircle2, ArrowRight, Store, User, Package, Clock } from "lucide-react"; 
import { Badge } from "@shadcn/components/ui/badge";

import { useTaskCard } from "@zustand/useTaskCard";
import { useEffect } from "react";
import { getLabel, getShortLabel } from "@pages/JIFI/warranty-claim";
import { FAULT_REASONS } from "@pages/JIFI/warranty-claim/warranty-claim";



const priorityColors = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-gray-100 text-gray-800'
};

export function TaskCard({ task, isOverlay }) {
    
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
        id: task.id, 
        data: {
            type: "Task",
            task, 
        },
        attributes: {
            roleDescription: "Task",
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : 'auto'
    };


    const selectedCard = useTaskCard ((state) => state.selectedCard);
    const selected = useTaskCard((state => state.selected))
    const taskCard  = useTaskCard((state => state.task))
    const setSelected = useTaskCard((state => state.setSelected))
    useEffect(() => {
        if(task.selected) {
            if(selected === undefined) {
                selectedCard({...task})
                setSelected(true)
            }
        }
    },[])
    
    const cardClass = task?.id === taskCard?.id
        ? "border-2 border-gray-900 shadow-lg bg-gray-100"
        : "bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300";

    const variants = cva("", {
        variants: {
            dragging: {
                over: "ring-2 opacity-30", 
                overlay: "ring-2 ring-gray-400",
            },
        },
    });
    const formattedDate = task.replacementIssuedDate ? new Date(task.replacementIssuedDate).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }) : '';

    return (
        <Card
            onClick={() => {
                selectedCard(task)
                setSelected(true)
            }}
            ref={setNodeRef}
            style={style}
            className={`${cardClass} cursor-pointer ${variants({
                dragging: isOverlay ? "overlay" : isDragging ? "over" : undefined,
            })} transition-all duration-300 rounded-xl hover:shadow-lg border-l-4 border-gray-700 hover:border-l-gray-900`}
        >
            <CardHeader 
                {...attributes}
                {...listeners}
                className="px-4 py-3 flex flex-row items-center justify-between border-b cursor-grab bg-gradient-to-r from-gray-50 to-gray-100"
            >
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-600" />
                        <span className="font-semibold text-sm text-gray-900">
                            {task.customerName}
                        </span>
                    </div>
                    
                    {task.batchCode && (
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-600" />
                            <span className="text-xs text-gray-700 font-medium">
                                Batch: {task.batchCode}
                            </span>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-2 flex-wrap">
                        {task?.order?.name &&
                            <Badge variant="outline" className="text-xs py-0.5 px-2 border-gray-300 bg-gray-100 text-gray-700">
                                {task?.order?.name}
                            </Badge>
                        }
                        {!task?.hasOrder && (
                            <>
                            {task?.orderNumber && <Badge variant="outline" className="text-xs bg-gray-100 text-gray-800 mr-1">
                                {task?.orderNumber}
                            </Badge>}
                            <Badge variant="outline" className="text-xs bg-gray-100 text-gray-800">
                                Pre 2020
                            </Badge>
                            </>
                        )}
                    </div>
                    
                    
                    {task.pickupLocation && (
                        <div className="flex items-center gap-1.5 bg-gray-200 px-2 py-1 rounded-full">
                            <Store className="h-4 w-4 text-gray-700" />
                            <span className="text-xs text-gray-800 font-medium">
                                Pickup: {task?.pickupLocation?.display_name} ({task?.pickupLocation?.code})
                            </span>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-2">
                    {task.toBeShipped && <Truck className="h-5 w-5 text-gray-600" />}
                    {task.fulfilled && <CheckCircle2 className="h-5 w-5 text-gray-900" />}
                </div>
            </CardHeader>

            <CardContent className="p-4 space-y-3">
              
                <div className="flex items-center gap-2">
                    <Badge 
                        variant="destructive" 
                        className="text-xs font-medium max-w-full truncate" 
                        title={getLabel(FAULT_REASONS, task.faultReason)}
                    >
                        {getShortLabel(FAULT_REASONS, task.faultReason)}
                    </Badge>
                </div>

                
                {task?.replacementSkus?.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                            <Package className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">Replacement Items</span>
                        </div>
                        
                        {task.replacementSkus.filter(replacement => replacement !== null).map((item, index) => (
                            
                            <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                            
                                <div className="flex items-center gap-2 text-xs mb-2">
                                    <span className="text-gray-500 line-through font-medium">{item?.originalSku}</span>
                                    <ArrowRight className="h-3 w-3 text-gray-400" />
                                    <span className="font-semibold text-gray-900">
                                        {item?.replacementSku}
                                    </span>
                                </div>
                                
                            
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {item.toBeShipped ? (
                                            <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
                                                <Truck className="h-3 w-3 text-gray-600" />
                                                <span className="text-xs text-gray-700 font-medium">Ship</span>
                                            </div>
                                        ) : item.pickupLocation ? (
                                            <div className="flex items-center gap-1 bg-gray-200 px-2 py-1 rounded-full">
                                                <Store className="h-3 w-3 text-gray-700" />
                                                                                <span className="text-xs text-gray-800 font-medium">
                                    {item.pickupLocation?.display_name}
                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                
                                
                                {item.dateNeededBy && (
                                    <div className="flex items-center gap-1 text-xs text-gray-600 mt-2">
                                        <Clock className="h-3 w-3" />
                                        <span>Needed by: </span>
                                        <span className="font-medium">
                                            {new Date(item.dateNeededBy).toLocaleDateString('en-AU', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                
                <div className="flex items-center justify-between text-xs text-gray-500 border-t pt-3">
                    <div className="flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" />
                        <span className="font-medium">
                            {formattedDate ? `Replaced: ${formattedDate}` : 'Pending'}
                        </span>
                    </div>
                    
                    {task.priority && (
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${priorityColors[task.priority]}`}>
                            <AlertCircle className="h-4 w-4" />
                            <span className="capitalize">{task.priority}</span>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
